import { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

interface AudioPlayerProps {
    audioUrl: string;
}

const AudioPlayer = ({ audioUrl }: AudioPlayerProps) => {
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [currentTime, setCurrentTime] = useState<number>(0);
    const [duration, setDuration] = useState<number>(0);
    const [dragging, setDragging] = useState<boolean>(false);

    const handleTimeUpdate = () => {
        if (!dragging && audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
        }
    };

    const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
    };

    const togglePlay = () => {
        if (!audioRef.current) return;

        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    const handleLoadedMetadata = () => {
        if (audioRef.current) {
            setDuration(audioRef.current.duration);
        }
    };

    const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCurrentTime(parseFloat(e.target.value));
    };

    const handleSliderMouseDown = () => {
        setDragging(true);
    };

    const handleSliderMouseUp = () => {
        if (audioRef.current) {
            audioRef.current.currentTime = currentTime;
        }
        setDragging(false);
    };

    useEffect(() => {
        const audio = audioRef.current;
        if (audio) {
            audio.addEventListener('timeupdate', handleTimeUpdate);
            audio.addEventListener('ended', handleEnded);
            audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        }

        return () => {
            if (audio) {
                audio.removeEventListener('timeupdate', handleTimeUpdate);
                audio.removeEventListener('ended', handleEnded);
                audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            }
        };
    }, []);

    return (
        <div style={{ display: 'flex', alignItems: 'center', paddingRight: '64px' }}>
            <audio ref={audioRef} src={audioUrl} />
            <div style={{ width: '34px', height: '34px', marginRight: '10px' }} onClick={togglePlay}>
                {isPlaying ? (
                    <svg style={{ width: '34px', height: '34px' }} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="50" cy="50" r="45" fill="#7c7f71"/>
                        <rect x="35" y="30" width="10" height="40" fill="white"/>
                        <rect x="55" y="30" width="10" height="40" fill="white"/>
                    </svg>
                ) : (
                    <svg style={{ width: '34px', height: '34px' }} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="50" cy="50" r="45" fill="#7c7f71"/>
                        <polygon points="40,30 40,70 70,50" fill="white"/>
                    </svg>
                )}
            </div>
            <Slider
                type="range"
                min="0"
                max={duration}
                value={currentTime}
                onChange={handleSliderChange}
                onMouseDown={handleSliderMouseDown}
                onMouseUp={handleSliderMouseUp}
                step="0.1"
                progress={duration ? currentTime / duration : 0}
            />
        </div>
    );
};

const Slider = styled.input<{ progress: number }>`
    width: 100%;
    height: 5px;
    -webkit-appearance: none;
    background: #C4D8B6;
    outline: none;
    border-radius: 5px;
    transition: background 0.3s;

    &::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 15px;
        height: 15px;
        background: #7B8772;
        border-radius: 50%;
        cursor: pointer;
    }

    &::-moz-range-thumb {
        width: 15px;
        height: 15px;
        background: #7B8772;
        border-radius: 50%;
        cursor: pointer;
    }

    &::-ms-thumb {
        width: 15px;
        height: 15px;
        background: #7B8772;
        border-radius: 50%;
        cursor: pointer;
    }
`;

export default AudioPlayer;
